import { computed, Directive, DOCUMENT, inject, Injectable, OnDestroy, PLATFORM_ID, signal } from '@angular/core';
import {isPlatformBrowser} from "@angular/common";

// How do we do this better?  PrimeNG doesn't currently support injectable config...

function rP(rems: number): number {
  return rems * 16;
}

export interface LayoutConfigInterface {
  breakpoints: {
    sm: number;
    md: number;
    lg: number;
    xl: number;
    xxl: number;
  }
}

export const DefaultLayoutConfig: LayoutConfigInterface = {
  breakpoints: {
    sm: rP(40),
    md: rP(48),
    lg: rP(64),  //1024px
    xl: rP(80),
    xxl: rP(96)
  }
}

// Move to hui

/**
 * Service used to coordinate the top bar, sidebar(s), and mobile hamburger menu button.
 * This is a bastardization of PrimeNG Demo code....
 */
@Directive()
export class HngLayoutControlServiceBase implements OnDestroy {
  protected platformId: any = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  protected resizeListener: ((event: Event) => void) | null = null;
  protected layoutConfig: LayoutConfigInterface;
  public readonly screenSize = signal(0);

  constructor(config: LayoutConfigInterface) {
    this.layoutConfig = config;
    this.bindResizeListener();
  }

  ngOnDestroy() {
    this.unbindResizeListener();
  }

  /***** Screen Size Management ***************************************************************************************/

  // Should we be binding to Renderer2 like the root page layout does?
  private bindResizeListener() {
    if (isPlatformBrowser(this.platformId)) {
      this.assignScreenSize();
      if (!this.resizeListener) {
        this.resizeListener = (event) => {
          this.assignScreenSize();
        };

        window.addEventListener('resize', this.resizeListener);
      }
    }
  }

  private unbindResizeListener() {
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }
  }

  private assignScreenSize() {
    if (window) {
      this.screenSize.set(window.innerWidth);
    }
  }

  /**
   * We consider anything less than large a tablet, but not necessarily a 'smallScreen'
   */
  public isTablet = computed(() => {
    const width = this.screenSize();
    return (
      width <= this.layoutConfig.breakpoints.lg &&
      width > this.layoutConfig.breakpoints.sm
    );
  });

  public isMobile = computed(() => {
    const width = this.screenSize();
    return width <= this.layoutConfig.breakpoints.sm;
  });

  /**
   * Small screen is anything less than medium.  Some tablets are bigger than that...
   */
  public isSmallScreen = computed(() => {
    return this.screenSize() <= this.layoutConfig.breakpoints.md;
  });

  public isLargeScreen = computed(() => {
    return this.screenSize() > this.layoutConfig.breakpoints.md;
  });

  public screenSizeClass = computed(() => {
    const width = this.screenSize();
    if (width === 0) {
      return 'unknown';
    } else if (width <= this.layoutConfig.breakpoints.sm) {
      return 'xsm';
    } else if (width <= this.layoutConfig.breakpoints.md) {
      return 'sm';
    } else if (width <= this.layoutConfig.breakpoints.lg) {
      return 'md';
    } else if (width <= this.layoutConfig.breakpoints.xl) {
      return 'lg';
    } else if (width <= this.layoutConfig.breakpoints.xxl) {
      return 'xl';
    }
    return '2xl';
  });

  public setTheme(theme: string | undefined) {
    if (this.document) {
      this.document.documentElement.classList.remove('theme-light');
      this.document.documentElement.classList.remove('theme-dark');
      if (theme && theme !== 'device') {
        this.document.documentElement.classList.add(`theme-${theme}`);
      }

      if (this.document.documentElement.dataset) {
        delete this.document.documentElement.dataset['theme'];
        if (theme && theme !== 'device') {
          this.document.documentElement.dataset['theme'] = 'theme-' + theme;
        }
      }
    }
  }
}

@Injectable({providedIn: 'root'})
export class SplixerLayoutControlService extends HngLayoutControlServiceBase {

  constructor() {
    super(DefaultLayoutConfig);
  }

  /**
   * Classes to be applied to the 'layout-wrapper' element
   */
  public rootPageClasses = computed(() => {
    const classes = [];
    classes.push('screen-' + this.screenSizeClass());
    return classes;
  })

}
